import React, { useEffect, useState } from 'react';
import { StyledBox, StyledDiv, StyledSpan } from '../../Theme/CommonStyled';
import Card from '../Common/Card';
import { useTranslation } from 'react-i18next';
import Icon from '../Common/Icon';
import useLandingStore from '../../Store/Landing';
import { getCreditDetailsData } from '../../Services/creditDetails.service';
import Title from '../Common/Title';
import DetailsRow from '../Common/DetailsRow';
import { fixLocaleString } from '../../Utils/common.util';
import LoadingBox from '../Common/LoadingBox';
import { useParams } from 'react-router';
import DiscountIcon from '../../Assets/images/ICON - Discount.svg';
import { useLocation } from 'react-router-dom';
import { StyledCopiedTag } from '../Credits/CreditCard/style';
import { StyledCode } from './style';

const CreditDetails: React.FC = () => {
  const { t } = useTranslation();
  const params = useParams();
  const { state } = useLocation();

  const landingData = useLandingStore((state) => state.landingData);

  const [creditDetailsData, setCreditDetailsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [codeIsCopied, setCodeIsCopied] = useState(false);

  const getData = async (customerActivityId: number): Promise<void> => {
    setLoading(true);

    const response = await getCreditDetailsData(
      customerActivityId,
      landingData?.uniqueId
    );
    setCreditDetailsData(response);
    setLoading(false);
  };

  useEffect(() => {
    getData(Number(params?.customerActivityId));
  }, []);

  useEffect(() => {
    const codeCopyHandler = setTimeout(() => {
      codeIsCopied && setCodeIsCopied(false);
    }, 3000);

    return () => clearTimeout(codeCopyHandler);
  }, [codeIsCopied]);

  return (
    <LoadingBox loading={loading}>
      <StyledDiv pt={9} px={8} pb={18}>
        <Card mb={9}>
          <StyledBox mb="0 !important" justifyContent="space-between">
            <StyledDiv
              color="normalText"
              fontSize={4}
              display="flex"
              alignItems="center">
              <Icon
                name={
                  creditDetailsData?.activityDetails.status === 'Active'
                    ? 'ok_correct_working'
                    : creditDetailsData?.activityDetails.status === 'Used'
                    ? 'subtract_minus'
                    : 'danger_incorrect_notworking'
                }
                fontSize={12}
                color={
                  creditDetailsData?.activityDetails.status === 'Active'
                    ? 'secondaryGreen'
                    : creditDetailsData?.activityDetails.status === 'Used'
                    ? 'secondaryText'
                    : 'danger'
                }
              />
              <StyledSpan mr={6}>{t('creditDetails.creditStatus')}</StyledSpan>
            </StyledDiv>
            <StyledDiv
              color={
                creditDetailsData?.activityDetails.status === 'Active'
                  ? 'secondaryGreen'
                  : creditDetailsData?.activityDetails.status === 'Used'
                  ? 'secondaryText'
                  : 'danger'
              }>
              {creditDetailsData?.activityDetails.status
                ? t(
                    `creditDetails.status.${creditDetailsData?.activityDetails.status}`
                  )
                : '-'}
            </StyledDiv>
          </StyledBox>
          {!!state?.code &&
            creditDetailsData?.activityDetails.status === 'Active' && (
              <StyledBox
                mb="0 !important"
                mt={6}
                justifyContent="space-between">
                <StyledDiv
                  color="normalText"
                  fontSize={4}
                  display="flex"
                  alignItems="center">
                  {/*Todo: fix it after icon pack update*/}
                  <img src={DiscountIcon} alt="discountIcon" />
                  <StyledSpan mr={6}>{t('credits.creditCard.code')}</StyledSpan>
                </StyledDiv>
                <StyledCopiedTag
                  bg="secondaryBackground"
                  codeIsCopied={codeIsCopied}>
                  <Icon
                    name="check"
                    fontSize={10}
                    color="secondaryGreen"
                    ml={6}
                  />
                  <StyledSpan color="normalText" fontSize={2}>
                    {t('global.copied')}
                  </StyledSpan>
                </StyledCopiedTag>
                <StyledCode
                  codeIsCopied={codeIsCopied}
                  cursor="pointer"
                  color="primaryBlue"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(String(state?.code));
                    setCodeIsCopied(true);
                  }}>
                  <Icon
                    name="duplicate"
                    color="primaryBlue"
                    fontSize={8}
                    mr={6}
                  />
                  {state?.code}
                </StyledCode>
              </StyledBox>
            )}
          {creditDetailsData?.activityDetails?.validBranchName && (
            <StyledBox mt={6} justifyContent="space-between">
              <StyledDiv color="normalText" fontSize={4}>
                <Icon
                  name="location-outline"
                  color="secondaryText"
                  ml={6}
                  fontSize={10}
                />
                {t('creditDetails.usableIn')}
              </StyledDiv>
              <StyledDiv color="minorText">
                {creditDetailsData?.activityDetails?.validBranchName}
              </StyledDiv>
            </StyledBox>
          )}
        </Card>
        {creditDetailsData?.activityDetails?.creationTime && (
          <Card mb={9}>
            <Title
              title={t('creditDetails.scheduleUse')}
              color="normalText"
              mb="7"
            />
            <StyledDiv
              display="flex"
              width="100%"
              gap={10}
              // height="72px"
              mb={6}>
              {creditDetailsData?.activityDetails?.brandUseLimitationName && (
                <StyledBox
                  width="72px !important"
                  justifyContent="center"
                  mb="0 !important">
                  <Icon
                    name="calendar_month"
                    fontSize={16}
                    color="primaryBlue"
                  />
                </StyledBox>
              )}
              <StyledDiv flex={1}>
                {creditDetailsData?.activityDetails?.brandUseLimitationName && (
                  <StyledBox
                    justifyContent="space-between"
                    color="normalText"
                    fontSize={2}>
                    {creditDetailsData?.activityDetails?.brandUseLimitationName}
                  </StyledBox>
                )}
                <StyledBox width="unset !important" flex={1} mb="0 !important">
                  <DetailsRow
                    title={
                      <StyledDiv>
                        {!creditDetailsData?.activityDetails
                          ?.brandUseLimitationName && (
                          <Icon
                            name="calendar_month"
                            fontSize={8}
                            ml={6}
                            color="primaryBlue"
                          />
                        )}
                        {t('creditDetails.expireTime')}:
                      </StyledDiv>
                    }
                    value={
                      <StyledDiv
                        color="danger"
                        fontSize={2}
                        letterSpacing="0.1rem">
                        {new Date(
                          creditDetailsData?.activityDetails.expireTime
                        ).toLocaleString('fa-IR', {
                          year: 'numeric',
                          month: 'numeric',
                          day: 'numeric',
                          calendar: 'persian'
                        })}
                      </StyledDiv>
                    }
                  />
                </StyledBox>
              </StyledDiv>
            </StyledDiv>
          </Card>
        )}
        {(!!creditDetailsData?.activityDetails?.credit ||
          !!creditDetailsData?.activityDetails?.remainCredit) && (
          <Card mb={9}>
            <Title title={t('credits.credit')} color="normalText" mb="7" />
            <StyledDiv display="flex" gap={10} width="100%" height="72px">
              <StyledBox
                width="72px !important"
                justifyContent="center"
                mb="0 !important">
                <Icon name="credit" fontSize={18} color="primaryGreen" />
              </StyledBox>
              <StyledBox
                width="unset !important"
                flex={1}
                flexDirection="column"
                mb="0 !important">
                {!!creditDetailsData?.activityDetails?.credit && (
                  <DetailsRow
                    mb={6}
                    title={`${t('creditDetails.creditPrice')}:`}
                    value={
                      <StyledDiv color="secondaryText" fontSize={2}>
                        {fixLocaleString(
                          creditDetailsData?.activityDetails.credit
                        )}
                        <StyledSpan mr={3} color="minorText" fontSize="0.8rem">
                          {!!creditDetailsData?.activityDetails.credit &&
                            landingData.currency}
                        </StyledSpan>
                      </StyledDiv>
                    }
                  />
                )}
                {!!creditDetailsData?.activityDetails?.remainCredit && (
                  <DetailsRow
                    title={`${t('creditDetails.creditRemain')}:`}
                    value={
                      <StyledDiv color="secondaryGreen" fontSize={2}>
                        {fixLocaleString(
                          creditDetailsData?.activityDetails.remainCredit
                        )}
                        <StyledSpan mr={3} color="minorText" fontSize="0.8rem">
                          {!!creditDetailsData?.activityDetails.remainCredit &&
                            landingData.currency}
                        </StyledSpan>
                      </StyledDiv>
                    }
                  />
                )}
              </StyledBox>
            </StyledDiv>
          </Card>
        )}
        {(!!creditDetailsData?.activityDetails?.percentage ||
          !!creditDetailsData?.activityDetails?.maxDiscountPrice ||
          !!creditDetailsData?.activityDetails?.percentageCount ||
          !!creditDetailsData?.activityDetails?.percentageCountRemain) && (
          <Card mb={9}>
            <Title title={t('global.discount')} color="normalText" mb="7" />
            <StyledDiv display="flex" width="100%" gap={10}>
              <StyledBox width="72px !important" justifyContent="center">
                <Icon name="discount" fontSize={16} color="primaryPurple" />
              </StyledBox>
              <StyledDiv flex={1}>
                {(!!creditDetailsData?.activityDetails?.percentage ||
                  !!creditDetailsData?.activityDetails?.maxDiscountPrice) && (
                  <StyledBox flexDirection="column">
                    {!!creditDetailsData?.activityDetails?.percentage && (
                      <DetailsRow
                        mb={6}
                        title={`${t('creditDetails.discountPercentage')}:`}
                        value={
                          <StyledDiv color="secondaryGreen" fontSize={2}>
                            {creditDetailsData?.activityDetails.percentage}
                            <StyledSpan
                              mr={3}
                              color="minorText"
                              fontSize="0.8rem">
                              {t('global.percent')}
                            </StyledSpan>
                          </StyledDiv>
                        }
                      />
                    )}
                    {!!creditDetailsData?.activityDetails?.maxDiscountPrice && (
                      <DetailsRow
                        title={`${t('creditDetails.maxDiscountPrice')}:`}
                        value={
                          <StyledDiv fontSize={2}>
                            {
                              creditDetailsData?.activityDetails
                                .maxDiscountPrice
                            }
                            <StyledSpan
                              mr={3}
                              color="minorText"
                              fontSize="0.8rem">
                              {!!creditDetailsData?.activityDetails
                                .maxDiscountPrice && landingData.currency}
                            </StyledSpan>
                          </StyledDiv>
                        }
                      />
                    )}
                  </StyledBox>
                )}
                {(!!creditDetailsData?.activityDetails?.percentageCount ||
                  !!creditDetailsData?.activityDetails
                    ?.percentageCountRemain) && (
                  <StyledBox flexDirection="column">
                    {!!creditDetailsData?.activityDetails?.percentageCount && (
                      <DetailsRow
                        mb={6}
                        title={`${t('creditDetails.numberOfDiscount')}:`}
                        value={
                          <StyledDiv fontSize={2}>
                            {creditDetailsData?.activityDetails.percentageCount}
                            <StyledSpan
                              mr={3}
                              color="minorText"
                              fontSize="0.8rem">
                              {t('creditDetails.time')}
                            </StyledSpan>
                          </StyledDiv>
                        }
                      />
                    )}
                    {!!creditDetailsData?.activityDetails
                      ?.percentageCountRemain && (
                      <DetailsRow
                        title={`${t('creditDetails.numberOfRemain')}:`}
                        value={
                          <StyledDiv color="secondaryGreen" fontSize={2}>
                            {
                              creditDetailsData?.activityDetails
                                .percentageCountRemain
                            }
                            <StyledSpan
                              mr={3}
                              color="minorText"
                              fontSize="0.8rem">
                              {t('creditDetails.time')}
                            </StyledSpan>
                          </StyledDiv>
                        }
                      />
                    )}
                  </StyledBox>
                )}
              </StyledDiv>
            </StyledDiv>
          </Card>
        )}
      </StyledDiv>
    </LoadingBox>
  );
};

export default CreditDetails;
